import { useEffect } from 'react';
import { FileFormatEnum } from 'types/generated/client';
import BackGallery from 'svg/BackGallery';
import GalleryLeft from 'svg/GalleryLeft';
import GalleryRight from 'svg/GalleryRight';

export interface LightboxItem {
  url: string;
  /** Anything other than VIDEO renders as an image, so callers without a format
   *  column can leave it out. */
  format?: FileFormatEnum | null;
}

interface Props {
  items: LightboxItem[];
  /** Index of the item on screen; the caller owns it so the strip can open at a tile. */
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}

/**
 * The full-screen media viewer. Extracted from `MediaGallery` because the coach
 * page, the club page and the player profile each need it behind a different
 * strip — only the viewer is shared, the grids are per-design.
 */
export default function MediaLightbox({ items, index, onIndexChange, onClose }: Props) {
  const count = items.length;
  const showPrev = () => onIndexChange((index - 1 + count) % count);
  const showNext = () => onIndexChange((index + 1) % count);

  // Lock the page behind the overlay and drive it from the keyboard while open.
  useEffect(() => {
    document.body.classList.add('overflow-hidden');

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') showPrev();
      if (event.key === 'ArrowRight') showNext();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [index, count, onClose]);

  const current = items[index];
  if (!current) return null;

  return (
    <div className="fixed inset-0 z-60 flex h-screen flex-col overflow-hidden bg-color-bg-lightmode-invert dark:bg-color-bg-darkmode-invert">
      <div className="relative shrink-0 border-b border-b-brand-gray-700 px-10 py-10 dark:border-b-brand-gray-300">
        <BackGallery
          onClick={onClose}
          className="absolute top-1/2 h-10 w-10 -translate-y-1/2 cursor-pointer text-color-text-lightmode-invert dark:text-color-text-darkmode-invert"
        />
        <span className="typography-product-heading absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-color-text-lightmode-invert dark:text-color-text-darkmode-invert">
          {index + 1}/{count}
        </span>
      </div>

      <div className="relative flex flex-shrink-0 flex-grow justify-center gap-5 px-10">
        <span
          onClick={showPrev}
          className="absolute top-1/2 z-60 -translate-y-1/2 cursor-pointer rounded-full border border-color-border-card-lightmode bg-color-bg-lightmode-invert p-3 dark:border-color-border-card-darkmode dark:bg-color-bg-darkmode-invert xs:left-[5%] sm:left-[5%] lg:left-[10%]"
        >
          <GalleryLeft className="h-5 w-5 text-color-text-lightmode-invert dark:text-color-text-darkmode-invert" />
        </span>

        <div className="relative flex h-full w-full items-center lg:max-w-[640px]">
          <div className="relative flex h-full w-full items-center justify-center">
            {current.format === FileFormatEnum.Video ? (
              <video className="h-[80vh] rounded-lg" src={current.url} controls />
            ) : (
              <img className="h-[80vh] rounded-lg" src={current.url} alt={`Item ${index + 1}`} />
            )}
          </div>
        </div>

        <span
          onClick={showNext}
          className="absolute top-1/2 z-60 -translate-y-1/2 cursor-pointer rounded-full border border-color-border-card-lightmode bg-color-bg-lightmode-invert p-3 text-color-text-lightmode-invert dark:border-color-border-card-darkmode dark:bg-color-bg-darkmode-invert dark:text-color-text-darkmode-invert xs:right-[5%] sm:right-[5%] md:right-[10%]"
        >
          <GalleryRight className="h-5 w-5 text-color-text-lightmode-invert dark:text-color-text-darkmode-invert" />
        </span>
      </div>
    </div>
  );
}
